import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ShieldCheck, Heart, Search, ChevronDown, Play, Flame, Compass, Trophy, Users, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import XPNavIndicator from "@/components/XPNavIndicator";

// Grouped navigation: primary items live at root, "Explore" and "Community" become submenus
const PRIMARY = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/videos", label: "Videos", testid: "nav-videos" },
];
const EXPLORE = [
  { to: "/trending", label: "Trending", testid: "nav-trending", Icon: Flame, desc: "What's hot right now" },
  { to: "/routes", label: "Routes", testid: "nav-routes", Icon: Compass, desc: "Character storylines" },
];
const COMMUNITY = [
  { to: "/leaderboards", label: "Ranks", testid: "nav-ranks", Icon: Trophy, desc: "Top members this week" },
  { to: "/members", label: "Members", testid: "nav-members", Icon: Users, desc: "Search and browse users" },
  { to: "/patreon", label: "Patreon", testid: "nav-patreon", Icon: Star, desc: "Exclusive content & perks" },
];

function DropdownButton({ label, items, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold tracking-wide transition-colors ${open ? "text-white" : "text-zinc-400 hover:text-white"}`}
      >
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid={`${testid}-menu`}
          className="absolute left-0 top-full pt-2 w-[280px] z-50"
        >
          <div className="glass-strong rounded-xl overflow-hidden shadow-2xl border border-white/10 p-1.5">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/5 group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#E50914]/15 border border-[#E50914]/30 flex items-center justify-center text-[#E50914] shrink-0 group-hover:bg-[#E50914]/25">
                  <it.Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white">{it.label}</div>
                  <div className="text-[11px] text-zinc-400 line-clamp-1">{it.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `relative px-3 py-2 text-sm font-semibold tracking-wide transition-colors ${
      isActive ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  const onSearch = (e) => {
    e.preventDefault();
    const t = q.trim();
    navigate(t ? `/videos?q=${encodeURIComponent(t)}` : "/videos");
  };

  return (
    <header data-testid="main-navbar" className="sticky top-0 z-50 backdrop-blur-2xl bg-black/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
          <img
            src="/savageboy-icon-v2.png?v=2"
            alt="SavageBoy"
            className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(229,9,20,0.5)]"
          />
          <div className="font-display font-black text-white text-lg tracking-tight leading-none">
            Savage<span className="text-[#E50914]">Boy</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-0 ml-2">
          {PRIMARY.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid} end={n.to === "/"}>
              {({ isActive }) => (
                <span className="relative">
                  {n.label}
                  {isActive && <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#E50914] rounded-full" />}
                </span>
              )}
            </NavLink>
          ))}
          <DropdownButton label="Explore" items={EXPLORE} testid="nav-explore" />
          <DropdownButton label="Community" items={COMMUNITY} testid="nav-community" />
        </nav>

        <form onSubmit={onSearch} className="hidden lg:flex flex-1 max-w-md ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            data-testid="nav-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search videos, routes..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm text-white placeholder:text-zinc-500 outline-none"
          />
        </form>

        <div className="flex items-center gap-2 ml-auto lg:ml-0">
          {user && <XPNavIndicator />}
          {user && <NotificationBell />}
          {user ? (
            <div className="relative">
              <button
                data-testid="profile-menu-trigger"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E50914] to-[#7a0309] flex items-center justify-center text-xs font-black uppercase overflow-hidden">
                  {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.name || user.email)[0]}
                </span>
                <span className="hidden sm:block text-sm font-semibold text-white pr-2">
                  {user.name || user.email.split("@")[0]}
                </span>
              </button>
              {menuOpen && (
                <div data-testid="profile-menu" className="absolute right-0 mt-2 w-56 glass-strong rounded-xl overflow-hidden shadow-2xl" onMouseLeave={() => setMenuOpen(false)}>
                  <Link to="/profile" data-testid="menu-profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-200 hover:bg-white/5 hover:text-white">
                    <User className="w-4 h-4" /> My Profile
                  </Link>
                  <Link to="/profile" data-testid="menu-liked" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-200 hover:bg-white/5 hover:text-white">
                    <Heart className="w-4 h-4" /> Liked Videos
                  </Link>
                  {user.role === "admin" && (
                    <>
                      <Link to="/admin" data-testid="menu-admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-[#E50914] hover:bg-[#E50914]/10">
                        <ShieldCheck className="w-4 h-4" /> Admin Dashboard
                      </Link>
                      <Link to="/admin/stats" data-testid="menu-stats" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-[#E50914] hover:bg-[#E50914]/10">
                        <ShieldCheck className="w-4 h-4" /> Statistics
                      </Link>
                    </>
                  )}
                  <button data-testid="menu-logout" onClick={async () => { setMenuOpen(false); await logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-200 hover:bg-white/5 hover:text-white border-t border-white/5">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/register"
                data-testid="nav-register"
                className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-bold transition-colors"
              >
                Register
              </Link>
              <Link
                to="/login"
                data-testid="nav-login"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-bold transition-colors"
              >
                <User className="w-4 h-4" /> Login
              </Link>
            </div>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="md:hidden p-2 text-white">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-black/95">
          <div className="px-4 py-3 space-y-1">
            <form onSubmit={(e) => { onSearch(e); setOpen(false); }} className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </form>
            {[...PRIMARY, ...EXPLORE, ...COMMUNITY].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                end={n.to === "/"}
                data-testid={`m-${n.testid}`}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-[#E50914]/10 text-white" : "text-zinc-300 hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {!user && (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Link to="/register" onClick={() => setOpen(false)} data-testid="m-nav-register" className="block px-3 py-2.5 rounded-lg text-sm font-bold bg-white/5 border border-white/10 text-white text-center">
                  Register
                </Link>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="m-nav-login" className="block px-3 py-2.5 rounded-lg text-sm font-bold bg-[#E50914] text-white text-center">
                  Login
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
